import { Button, LoadingCard, Navigation, useWizardContext } from '@odmonorepo/core-components';
import TermLoanOfferCard from '../Assets/TermLoanOfferCard';
import LocOfferCard from '../Assets/LocOfferCard';
import { FormattedMessage } from 'react-intl';
import { Stack } from 'react-bootstrap';
import { useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useApplication } from 'merchant-api/unified';
import { detailedOfferProps, PricingResponse, selectOffer, selectedOfferIdAtom, useDetailedOffers } from 'merchant-api/offer';
import { checkoutSteps } from '../../CheckoutWizard';
import { getCheckoutState, startCheckout } from 'merchant-api/checkout';
import { useQueryClient } from '@tanstack/react-query';
import { atom, useSetAtom } from 'jotai';

const PRODUCT_TYPE_TERM_LOAN = 'TermLoan';
const PRODUCT_TYPE_LINE_OF_CREDIT = 'LineOfCredit';

export const isTermLoan = atom(false);

export const OfferReview = () => {
  const { applicationData, isLoadingApplication, isErrorApplication } = useApplication();
  const { selectedOffer, experienceType, isLoadingDetailedOffers, isErrorOffers, mutate, partnerAccountName, partnerAccountDba } = useDetailedOffers();
  const { checkoutData, isErrorCheckoutState, isLoadingCheckoutState } = getCheckoutState();
  const { start, starting } = startCheckout();
  const navigate = useNavigate();
  const { nextStep } = useWizardContext<typeof checkoutSteps>();
  const queryClient = useQueryClient();
  const queryKey = ['detailedOffers'];
  const setTermLoan = useSetAtom(isTermLoan);
  const setSelectedOfferId = useSetAtom(selectedOfferIdAtom);
  const isDirectNewApp = applicationData?.type === 'NEW' && applicationData?.metadata?.channelCode === 'DIRECT';

  // Clear any stored offerId so useDetailedOffers fetches all offers here,
  // including when the user navigates back after having already accepted an offer.
  useEffect(() => {
    setSelectedOfferId(null);
  }, []);

  setTermLoan(selectedOffer?.product === PRODUCT_TYPE_TERM_LOAN);

  useEffect(() => {
    if (checkoutData?.checkoutStatus === 'COMPLETED') {
      navigate({ to: '/' });
    }
  }, [checkoutData]);

  const { select } = selectOffer(selectedOffer?.offerId);

  if (isLoadingDetailedOffers || isLoadingApplication || isLoadingCheckoutState || starting || queryClient.isMutating({ mutationKey: ['detailedOffers'] })) return <LoadingCard />;

  if (isErrorApplication) throw new Error('Error fetching application data');
  if (isErrorOffers) throw new Error('Error fetching offers');
  if (isErrorCheckoutState) throw new Error('Error fetching checkout state');
  if (!selectedOffer) return <LoadingCard />;

  const partnerName = partnerAccountDba || partnerAccountName || undefined;

  const handleAccept = () =>
    select({
      ...selectedOffer?.pricedDetails,
      ppd: selectedOffer?.prePaymentDiscountPct === 100 ? 100 : 25,
    } as PricingResponse)
      .then(() => start())
      .then(() => {
        setSelectedOfferId(selectedOffer.offerId);
        nextStep();
      })
      .catch((e) => {
        throw new Error(e);
      });

  return (
    <Stack gap={6} style={{ maxWidth: '600px' }} className="mx-auto">
      <h2 className="text-secondary text-center fs-3">
        <FormattedMessage id="offerReview.title" />
      </h2>
      {selectedOffer &&
        (selectedOffer.product === PRODUCT_TYPE_TERM_LOAN ? (
          <TermLoanOfferCard offer={selectedOffer} applicationType={applicationData?.type!} partnerName={partnerName} />
        ) : (
          <LocOfferCard offer={selectedOffer} partnerName={partnerName} />
        ))}
      <div className="fs-8 mx-auto" style={{ maxWidth: '600px' }}>
        <FormattedMessage
          id="offerReview.locDisclaimer"
          values={{
            link: () => {
              return (
                <a href="https://www.ondeck.com/terms" target="_blank" rel="noreferrer">
                  Terms of Use
                </a>
              );
            },
          }}
        />
      </div>

      <Navigation
        validate={false}
        continueOnly={experienceType !== 'configurable' || selectedOffer?.product === PRODUCT_TYPE_LINE_OF_CREDIT || isDirectNewApp}
        handleNext={() => (checkoutData && checkoutData.offerUuid === selectedOffer?.offerId ? nextStep() : handleAccept())}
        customBackBtn={
          <Button
            variant="outline-primary"
            style={{ width: '100%' }}
            onClick={() => {
              mutate(
                (current) => ({
                  ...current,
                  applicationOffer: {
                    ...current?.applicationOffer,
                    selectedOffer: null,
                  },
                }),
                false,
              )
                .then(() => {
                  queryClient.setQueryData(queryKey, (prev: detailedOfferProps) => ({
                    ...prev,
                    data: {
                      ...prev.data,
                      applicationOffer: {
                        ...prev.data.applicationOffer,
                        selectedOffer: null,
                      },
                    },
                  }));
                })
                .then(() => navigate({ to: '/application/$id', params: { id: applicationData!.id } }));
            }}
          >
            <FormattedMessage id="offerReview.modify" />
          </Button>
        }
        continueText={<FormattedMessage id="offerReview.acceptOffer" />}
      />
    </Stack>
  );
};

export default OfferReview;
